import {Injectable} from "@angular/core";
import {AceLogger} from "@logging/ace-logger";
import {forkJoin, isObservable, Observable, of} from "rxjs";
import {AceLoggerService} from "@logging/ace-logger.service";
import {Meter} from "@app/graphql-typings/graphql";
import {map, shareReplay, tap} from "rxjs/operators";
import {ArrayUtils, CollectionUtils} from "@shared/util/collections/collection-utils";
import {MetersService} from "@app/layout/sitemanager/meters/meters.service";


type SiteMetersCache = Map<number, Meter> | Observable<Map<number, Map<number, Meter>>>;


@Injectable()
export class MetersCache {

  private readonly AVAILABLE_SITE_ID_PLACEHOLDER = Number.NEGATIVE_INFINITY;

  private readonly logger: AceLogger;
  private readonly metersByIdBySiteId: Map<number, SiteMetersCache> = new Map();
  private readonly historyAvailableByNmiId: Map<number, boolean | Observable<Meter[]>> = new Map();

  constructor(private metersService: MetersService,
              logService: AceLoggerService) {
    this.logger = logService.create(MetersCache.name);
  }

  public updateMeterInCache(newMeter: Meter, oldMeterSiteId: number = newMeter.site_id): Observable<Meter> {
    const isSiteChanged = oldMeterSiteId !== newMeter.site_id;
    const observables = new Set<Observable<Map<number, Map<number, Meter>>>>();
    const newMeterSiteId = newMeter.site_id;
    if (this.hasSiteMetersCache(newMeterSiteId)) {
      const obs = this.getSiteMetersCache(this.metersByIdBySiteId, newMeterSiteId);
      if (isObservable(obs)) {
        this.logger.debug('Updating meters cache is pending as the meters are being loaded. site id: ' + newMeterSiteId);
        observables.add(obs);
      }
    }
    if (isSiteChanged && this.hasSiteMetersCache(oldMeterSiteId)) {
      const obs = this.getSiteMetersCache(this.metersByIdBySiteId, oldMeterSiteId);
      if (isObservable(obs)) {
        this.logger.debug('Updating meters cache for site is pending as the meters are being loaded. site id: ' + oldMeterSiteId);
        observables.add(obs);
      }
    }

    if (observables.size) {
      return forkJoin(Array.from(observables)).pipe(map(() => this.syncUpdateMeterInCache(newMeter, oldMeterSiteId)));
    }
    return of(this.syncUpdateMeterInCache(newMeter, oldMeterSiteId));
  }

  public removeMeterFromCache(siteId: number, nmiId: number): Observable<void> {
    if (this.hasSiteMetersCache(siteId)) {
      const siteMetersCache = this.getSiteMetersCache(this.metersByIdBySiteId, siteId);
      if (isObservable(siteMetersCache)) {
        this.logger.debug('Removing meter with id ' + nmiId + ' from site meters cache is pending as the meters are being loaded. site id: ' + siteId);
        return siteMetersCache.pipe(map(() => {
          this.syncRemoveMeterFromCache(siteId, nmiId);
          return null;
        }))
      } else {
        this.syncRemoveMeterFromCache(siteId, nmiId);
      }
    } else {
      this.logger.debug('Skip removing meter with id ' + nmiId + ' from site meters cache as site meters cache does not exist. site id: ' + siteId);
    }
    return of(null);
  }

  public getMetersBySiteIds(siteIds: (number | null)[], ignoreCache = false): Observable<Map<number, Meter>> {
    const uniqueSiteIds = new Set(siteIds);
    const uncachedSiteIds: Set<number> = new Set();
    const cachedSiteMetersMap: Map<number, Map<number, Meter>> = new Map();
    const siteMetersFetches = new Set<Observable<Map<number, Map<number, Meter>>>>();
    uniqueSiteIds.forEach(siteId => {
      const siteMeters = this.getSiteMetersCache(this.metersByIdBySiteId, siteId);
      if (isObservable(siteMeters)) {
        siteMetersFetches.add(siteMeters);
      } else if (siteMeters && !ignoreCache) {
        this.setSiteMetersCache(cachedSiteMetersMap, siteId, siteMeters);
      } else {
        uncachedSiteIds.add(siteId);
      }
    });
    if (uncachedSiteIds.size) {
      siteMetersFetches.add(this.fetchAndCacheMetersFromAPI(Array.from(uncachedSiteIds)));
    }
    const obs = siteMetersFetches.size
      ? forkJoin(Array.from(siteMetersFetches)).pipe(map((apiResults: Map<number, Map<number, Meter>>[]) => {
        apiResults.forEach(mBySiteIdById =>
          mBySiteIdById.forEach((metersById, siteId) =>
            this.setSiteMetersCache(cachedSiteMetersMap, siteId, metersById)));
        return cachedSiteMetersMap;
      }))
      : of(cachedSiteMetersMap);
    return obs.pipe(map(metersByIdBySiteId => {
      if (metersByIdBySiteId.size === 1) {
        return <Map<number, Meter>>this.getSiteMetersCache(metersByIdBySiteId, uniqueSiteIds.values().next().value);
      }
      const metersById = new Map();
      metersByIdBySiteId.forEach(mById => mById.forEach((meter, meterId) => {
        metersById.set(meterId, meter);
      }));
      return metersById;
    }));
  }

  private getSiteIdKey(siteId: number | null): number {
    return siteId == null ? this.AVAILABLE_SITE_ID_PLACEHOLDER : siteId;
  }

  private setSiteMetersCache(metersByIdBySiteId: Map<number, SiteMetersCache>, siteId: number | null, siteMetersCache: SiteMetersCache): void {
    metersByIdBySiteId.set(this.getSiteIdKey(siteId), siteMetersCache);
  }

  private getSiteMetersCache(metersByIdBySiteId: Map<number, SiteMetersCache>, siteId: number | null): SiteMetersCache {
    return metersByIdBySiteId.get(this.getSiteIdKey(siteId));
  }

  private hasSiteMetersCache(siteId: number | null): boolean {
    return this.metersByIdBySiteId.has(this.getSiteIdKey(siteId));
  }

  private syncUpdateMeterInCache(newMeter: Meter, oldMeterSiteId: number): Meter {
    const isSiteChanged = newMeter.site_id !== oldMeterSiteId;
    const newMeterSiteId = newMeter.site_id;
    const meterId = newMeter.nmi_id;
    if (isSiteChanged && this.hasSiteMetersCache(oldMeterSiteId)) {
      const oldSiteMetersById = this.getSiteMetersCache(this.metersByIdBySiteId, oldMeterSiteId);
      if (isObservable(oldSiteMetersById)) {
        throw new Error('Cannot remove meter with id ' + meterId + ' from old site meters cache as site meters cache is still being loaded. site id: ' + oldMeterSiteId);
      }
      this.logger.debug('Removing meter with id ' + meterId + ' from old site meters cache. site id: ' + oldMeterSiteId);
      oldSiteMetersById.delete(meterId);
    } else {
      this.logger.debug('Skip removing meter with id ' + meterId + ' from old site meters cache as site meters cache does not exist. site id: ' + oldMeterSiteId);
    }
    if (this.hasSiteMetersCache(newMeterSiteId)) {
      const newSiteMetersById = this.getSiteMetersCache(this.metersByIdBySiteId, newMeterSiteId);
      if (isObservable(newSiteMetersById)) {
        throw new Error('Cannot update meter with id ' + meterId + ' in cache as the site cache is still being loaded. site id: ' + newMeterSiteId);
      }
      this.logger.debug('Adding meter with id ' + meterId + ' to site meters cache. site id: ' + newMeterSiteId);
      newSiteMetersById.set(meterId, newMeter);
    } else {
      this.logger.debug('Skip adding meter with id ' + meterId + ' to site meters cache as site meters cache does not exist. site id: ' + newMeterSiteId);
    }
    return newMeter;
  }

  private syncRemoveMeterFromCache(siteId: number, nmiId: number): void {
    this.logger.debug('Removing meter with id ' + nmiId + ' from site meters cache. site id: ' + siteId);
    const siteMetersCache = this.getSiteMetersCache(this.metersByIdBySiteId, siteId);
    if (isObservable(siteMetersCache)) {
      throw new Error('Cannot remove meter with id ' + nmiId + ' from cache as site cache is still being loaded. site id: ' + siteId);
    }
    siteMetersCache.delete(nmiId);
    this.historyAvailableByNmiId.delete(nmiId);
  }

  private fetchAndCacheMetersFromAPI(siteIds: (number | null)[]): Observable<Map<number, Map<number, Meter>>> {
    const nonNullSiteIds = [];
    let hasNullSiteId = false;

    siteIds.forEach(siteId => {
      if (siteId == null) {
        hasNullSiteId = true;
      } else {
        nonNullSiteIds.push(siteId);
      }
    });

    const meterFetches$: Observable<Meter[]>[] = [];
    if (hasNullSiteId) {
      this.logger.debug('Fetching meters with no site id');
      meterFetches$.push(this.metersService.getAllMeters({ available: true }));
    }
    if (nonNullSiteIds.length) {
      this.logger.debug('Fetching meters with site ids: ' + nonNullSiteIds);
      meterFetches$.push(this.metersService.getAllMeters({ site_ids: nonNullSiteIds }));
    }


    const obs = forkJoin(meterFetches$)
      .pipe(
        map((meters: Meter[][]) => this.populateMetersCache(ArrayUtils.flatten(meters), siteIds)),
        shareReplay(1)
      );
    siteIds.forEach(siteId => this.setSiteMetersCache(this.metersByIdBySiteId, siteId, obs));
    return obs;
  }

  private populateMetersCache(meters: Meter[], siteIds: (number | null)[]): Map<number, Map<number, Meter>> {
    const metersBySiteIdById = new Map<number, Map<number, Meter>>();
    meters.forEach(meter => {
      const siteId = meter.site_id;
      let metersById = <Map<number, Meter>>this.getSiteMetersCache(metersBySiteIdById, siteId);
      if (metersById == null) {
        metersById = new Map<number, Meter>();
        this.setSiteMetersCache(metersBySiteIdById, siteId, metersById);
      }
      metersById.set(meter.nmi_id, meter);
    });

    siteIds.forEach(siteId => {
      let metersById = this.getSiteMetersCache(metersBySiteIdById, siteId);
      if (metersById == null) {
        metersById = new Map();
        this.setSiteMetersCache(metersBySiteIdById, siteId, metersById);
      }
      this.setSiteMetersCache(this.metersByIdBySiteId, siteId, metersById);
    });

    return metersBySiteIdById;
  }

  public findMetersWithHistoryByNmiIds(nmiIds: number[], metersByNmiId: Map<number, Meter>): Observable<Meter[] | null> {
    let historyResolvedMetersCount = 0;
    const metersWithHistory: Meter[] = [];
    const inFlightMeterFetches: Set<Observable<Meter[]>> = new Set();
    const missingLatestHistoryNmiIds = [];

    nmiIds.forEach(nmiId => {
      if (this.historyAvailableByNmiId.has(nmiId)) {
        const historyAvailable = this.historyAvailableByNmiId.get(nmiId);
        if (isObservable(historyAvailable)) {
          inFlightMeterFetches.add(historyAvailable);
        } else {
          historyResolvedMetersCount++;
          if (historyAvailable) {
            metersWithHistory.push(metersByNmiId.get(nmiId));
          }
        }
      } else {
        missingLatestHistoryNmiIds.push(nmiId);
      }
    });

    let observable: Observable<Meter[]>;

    if (historyResolvedMetersCount === nmiIds.length) {
      return of(metersWithHistory);
    } else {
      if (missingLatestHistoryNmiIds.length) {
        const metersFetch$ = this.metersService.getAllMeters({nmi_ids: missingLatestHistoryNmiIds}, true)
          .pipe(
            tap((meters: Meter[]) => {
              CollectionUtils.indexIntoExistingBy(this.historyAvailableByNmiId, meters, 'nmi_id', meter => meter.history_available);
            }),
            shareReplay(1)
          );
        inFlightMeterFetches.add(metersFetch$);
        missingLatestHistoryNmiIds.forEach(nmiId => {
          this.historyAvailableByNmiId.set(nmiId, metersFetch$);
        });
      }
      observable = forkJoin(Array.from(inFlightMeterFetches))
        .pipe(map((result: Meter[][]) => ArrayUtils.flatten(result)));
    }

    return observable.pipe(map((meter: Meter[]) => meter?.length
      ? nmiIds.reduce((result: Meter[], nmiId: number) => {
        const historyAvailable = this.historyAvailableByNmiId.get(nmiId);
        const meter = metersByNmiId.get(nmiId);
        if (isObservable(historyAvailable)) {
          throw new Error('Meter data history for meter with id ' + meter.nmi_number + ' has not been resolved.');
        }
        if (historyAvailable) {
          result.push(meter);
        }
        return result;
      }, [])
      : []));
  }
}
